import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { SgButtonVariant, SgSize } from '../../models';

const CLASS_PREFIX = 'sg-';

@Component({
  selector: 'sg-button',
  templateUrl: './sg-button.component.html',
  styleUrls: ['./sg-button.component.scss'],
  standalone: false,
})
export class SgButtonComponent {
  @Input() size: SgSize = 'medium';
  @Input() text: string = '';
  @Input() variant: SgButtonVariant = 'primary';
  @Input() icon?: string;
  @Input() iconPosition: 'start' | 'end' = 'end';
  @Input() isLoading: boolean = false;
  /**
   * Accessible name for the button. Aliased to the `aria-label` ATTRIBUTE deliberately: written on
   * `<sg-button>` it lands on the host — a custom element with no role — while the focusable inner
   * `<button>` holds only an aria-hidden `<mat-icon>`, leaving every icon-only button unnamed. The
   * alias means the existing `aria-label="…"` call sites start working without being touched.
   */
  @Input('aria-label') ariaLabel?: string;
  /** Accessible description (e.g. why the button is disabled), read by screen readers via aria-describedby. */
  @Input() description?: string;
  @Input() cssClass?: string;
  @Input() fullWidth: boolean = false;
  @Input() get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
  }

  /** When disabled, stays focusable (aria-disabled) so its `description` is reachable.
   *  Bind `(onClick)`, not `(click)`, so clicks are blocked while disabled. */
  @Input() get disabledInteractive(): boolean {
    return this._disabledInteractive;
  }
  set disabledInteractive(value: BooleanInput) {
    this._disabledInteractive = coerceBooleanProperty(value);
  }

  /** @ignore */
  @Output() onClick = new EventEmitter<void>();

  @HostBinding('class.sg-button-full-width') get isFullWidth() {
    return this.fullWidth;
  }

  get isIconButton() {
    return this.icon && (!this.text || this.text.trim() === '');
  }

  get buttonInternalClasses(): string[] {
    return [
      'sg-action-btn',
      this.classPrefix + this.size,
      this.classPrefix + this.variant + '-btn',
      this.isIconButton ? 'sg-icon-btn' : '',
      this.disabled ? 'sg-disabled-btn' : '',
      this.isLoading ? 'sg-loading-btn' : '',
    ];
  }

  /** @ignore */
  private _disabled = false;
  /** @ignore */
  private _disabledInteractive = false;

  /** @ignore */
  readonly classPrefix = CLASS_PREFIX;

  private static nextDescriptionId = 0;
  /** @ignore */
  readonly descriptionId = `sg-button-description-${(SgButtonComponent.nextDescriptionId += 1)}`;

  constructor() {}

  /** @ignore */
  onButtonClick() {
    // An interactive-disabled button still receives clicks and Enter/Space; never act on them.
    if (this.disabled || this.isLoading) return;

    this.onClick.emit();
  }
}
